interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: Record<string, any>;
}

export function createSEOHead(seo: SEOData) {
  const baseUrl = "https://hometoq.com";

  return {
    meta: [
      // Title
      {
        title: seo.title,
      },
      // Basic SEO
      {
        name: "description",
        content: seo.description,
      },
      ...(seo.keywords
        ? [
            {
              name: "keywords",
              content: seo.keywords,
            },
          ]
        : []),
      // Open Graph
      {
        property: "og:title",
        content: seo.ogTitle || seo.title,
      },
      {
        property: "og:description",
        content: seo.ogDescription || seo.description,
      },
      {
        property: "og:url",
        content: seo.ogUrl || baseUrl,
      },
      {
        property: "og:image",
        content: seo.ogImage || `${baseUrl}/images/hero.webp`,
      },
      {
        property: "og:type",
        content: "website",
      },
      // Twitter
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: seo.twitterTitle || seo.ogTitle || seo.title,
      },
      {
        name: "twitter:description",
        content: seo.twitterDescription || seo.ogDescription || seo.description,
      },
      {
        name: "twitter:image",
        content:
          seo.twitterImage || seo.ogImage || `${baseUrl}/images/hero.webp`,
      },
    ],
    // Canonical séparé dans links
    ...(seo.canonical
      ? {
          links: [
            {
              rel: "canonical",
              href: seo.canonical,
            },
          ],
        }
      : {}),
    ...(seo.structuredData
      ? {
          scripts: [
            {
              type: "application/ld+json",
              children: JSON.stringify(seo.structuredData),
            },
          ],
        }
      : {}),
  };
}

// Données SEO par page
export const SEO_DATA = {
  HOME: {
    title: "Hometoq | Réservez un chef à domicile en France 👨‍🍳",
    description:
      "Hometoq est une plateforme de chef à domicile disponible partout en France - Réservez un chef pour chaque événement en quelques clics",
    keywords:
      "chef à domicile, chef privé, chef prive, chef a domicile, batch cooking, chef à domicile autour de moi, chef, cuisine à domicile, traiteur à domicile, cuisinier à domicile",
    canonical: "https://hometoq.com",
  },
  FIND_CHEF: {
    title: "Nos chefs à domicile | Trouvez le vôtre près de chez vous 👨‍🍳",
    description:
      "Découvrez nos chefs à domicile disponibles partout en France. Trouvez le bon chef, consultez ses menus, et profitez du moment",
    keywords:
      "trouver un chef, réserver un chef, réserver un chef à domicile, trouver un chef à domicile, trouver chef domicile, chef pour repas, chef privé domicile,réservation chef",
    canonical: "https://hometoq.com/trouver-un-chef",
  },
  FIND_MENUS: {
    title: "Menus de chefs à domicile | Trouvez le vôtre près de chez vous 👨‍🍳",
    description:
      "Découvrez nos menus de chef à domicile disponibles partout en France. Gastronomique, convivial, végétarien - Trouvez le bon menu pour votre occasion",
    keywords:
      "menu chef domicile, repas à domicile, menu gastronomique, cuisine française, menu personnalisé, menu chef privé",
    canonical: "https://hometoq.com/trouver-un-chef/menus",
  },
  BECOME_CHEF: {
    title: "Devenir chef à domicile HomeToq | Rejoignez la plateforme 👨‍🍳",
    description:
      "Devenir chef à domicile HomeToq - Développez votre activité, proposez vos menus, gagnez en visibilité et recevez des demandes qualifiées",
    keywords:
      "devenir chef domicile, travailler comme chef, travailler chef à domicile, devenir chef privé, proposer ses menus, plateforme chef",
    canonical: "https://hometoq.com/devenir-chef",
  },
  CONTACT: {
    title: "Contact HomeToq | Assistance & Centre d'ide",
    description:
      "Besoin d'aide ? Une question sur votre réservation, un chef ou nos services ? Accédez à notre centre d'aide ou contactez notre équipe facilement",
    keywords:
      "contact hometoq, support client, aide réservation chef, centre d'aide chef à domicile",
    canonical: "https://hometoq.com/contact",
  },
  REVIEWS: {
    title: "Avis clients HomeToq | Que pensent nos clients ?",
    description:
      "Découvrez les avis clients sur HomeToq : retours d'expérience de ceux qui ont réservé un chef à domicile pour un repas unique, chez eux",
    keywords:
      "avis hometoq, témoignage chef domicile, témoignages clients hometoq, avis chef privé",
    canonical: "https://hometoq.com/avis",
  },
  GIFT_CARD: {
    title: "Carte cadeau HomeToq | Offrez un chef à domicile",
    description:
      "Offrez une carte cadeau HomeToq - Une idée originale pour un dîner à domicile avec un chef privé. Expérience inoubliable, simple à offrir, valable 1 an",
    keywords:
      "carte cadeau chef, cadeau repas à domicile, idée cadeau, idée cadeau originale, offrir expérience culinaire, repas cadeau",
    canonical: "https://hometoq.com/carte-cadeau",
  },
  SIGNUP: {
    title: "Inscription HomeToq | Client ou chef à domicile",
    description:
      "Inscrivez-vous sur HomeToq - Réservez un chef à domicile chez vous, ou proposez vos services en tant que chef, rejoignez Hometoq dès maintenant",
    keywords:
      "inscription hometoq, créer compte, s'inscrire chef domicile, devenir chef à domicile, devenir membre chef, ouvrir compte client hometoq",
    canonical: "https://hometoq.com/inscription",
  },
  LOGIN: {
    title: "Connexion HomeToq | Accédez à votre espace client ou chef",
    description:
      "Connectez-vous à votre compte HomeToq en tant que client ou chef à domicile. Accès sécurisé à vos réservations, services et informations personnelles",
    keywords:
      "connexion hometoq, se connecter, accès client hometoq, accès chef hometoq, mon compte hometoq",
    canonical: "https://hometoq.com/connexion",
  },
} as const;

// Import pour createChefSlug
import { createChefSlug } from "./utils";

// Fonctions helpers pour les données dynamiques
export const createChefSEO = (chef: {
  firstName: string;
  workingCity: string;
  specialisation?: string | null;
  avatar?: string | null;
  banner?: string | null;
  id: number;
}) => {
  const chefName = chef.firstName;
  const chefCity = chef.workingCity;
  const chefSpecialty = chef.specialisation || "cuisine gastronomique";
  const canonicalSlug = createChefSlug(chefName, chef.id);

  return {
    title: `Chef ${chefName} à ${chefCity} | Chef à domicile HomeToq`,
    description: `Réservez ${chefName}, chef à domicile à ${chefCity}, spécialisé en ${chefSpecialty}. Cuisine maison, prestations sur mesure, partout à ${chefCity}.`,
    keywords: `chef à domicile ${chefCity}, chef privé ${chefCity}, ${chefSpecialty.toLowerCase()} à domicile, réserver chef ${chefCity}`,
    canonical: `https://hometoq.com/chefs/${canonicalSlug}`,
    ogTitle: `Chef ${chefName} à ${chefCity} - Spécialiste ${chefSpecialty}`,
    ogDescription: `Découvrez le profil de ${chefName}, chef à domicile expert en ${chefSpecialty} à ${chefCity}. Réservation rapide & sécurisée.`,
    ogImage:
      chef?.avatar || chef?.banner || "https://hometoq.com/images/hero.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: chefName,
      jobTitle: "Chef à domicile",
      description: `Chef professionnel spécialisé en ${chefSpecialty} à ${chefCity}`,
      image: chef?.avatar,
      address: {
        "@type": "PostalAddress",
        addressLocality: chefCity,
        addressCountry: "FR",
      },
      offers: {
        "@type": "Offer",
        description: `Service de chef à domicile à ${chefCity}`,
        priceCurrency: "EUR",
      },
    },
  };
};

export const createMenuSEO = (menu: {
  name: string;
  price: number;
  image?: string | null;
  id: number;
  chef?: { 
    name: string; 
    workingCity?: string | null; 
    specialisation?: string | null;
    [key: string]: any; // Pour permettre d'autres propriétés
  };
}) => {
  const menuName = menu.name;
  const chefName = menu.chef?.name || "nos chefs";
  const chefCity = menu.chef?.workingCity || "";
  const chefSpecialty = menu.chef?.specialisation || "cuisine gastronomique";
  const menuPrice = (menu.price / 100).toFixed(2);
  const menuSlug = createChefSlug(menuName, menu.id);

  return {
    title: `${menuName} | Menu du Chef ${chefName} à ${chefCity}`,
    description: `Découvrez "${menuName}", le menu signature du chef ${chefName}. Gastronomie ${chefSpecialty}, livré chez vous à ${chefCity}.`,
    keywords: `menu chef à domicile, ${menuName.toLowerCase()}, menu ${chefSpecialty.toLowerCase()}, chef ${chefName} ${chefCity}`,
    canonical: `https://hometoq.com/menus/${menuSlug}`,
    ogTitle: `${menuName} - Menu ${chefSpecialty} du chef ${chefName}`,
    ogDescription: `Savourez "${menuName}", imaginé par le chef ${chefName}, expert en ${chefSpecialty}. Livraison à domicile à ${chefCity}.`,
    ogImage: menu?.image || "https://hometoq.com/images/menu_default.webp",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "MenuItem",
      name: menuName,
      description: `${menuName} imaginé par le chef ${chefName}, spécialiste ${chefSpecialty}`,
      image: menu?.image,
      author: {
        "@type": "Person",
        name: chefName,
        jobTitle: "Chef à domicile",
      },
      offers: {
        "@type": "Offer",
        description: `Menu ${menuName} avec service de chef à domicile à ${chefCity}`,
        priceCurrency: "EUR",
        price: menuPrice,
        availability: "https://schema.org/InStock",
      },
    },
  };
};

export const createFindChefCitySEO = (cityName: string) => ({
  title: `Chef à Domicile à ${cityName} | Trouvez votre chef | HomeToQ`,
  description: `Trouvez un chef à domicile à ${cityName} pour vos repas privés, événements ou dîners en tête-à-tête. Réservez facilement parmi nos chefs disponibles.`,
  keywords: `chef à domicile ${cityName.toLowerCase()}, trouver chef privé ${cityName.toLowerCase()}, réserver chef ${cityName.toLowerCase()}, cuisinier à domicile ${cityName}`,
  canonical: `https://hometoq.com/trouver-un-chef/chefs`, // Page principale sans filtres
});

export const createFindMenusCitySEO = (cityName: string) => ({
  title: `Menus Chefs à Domicile à ${cityName} | HomeToq`,
  description: `Découvrez nos menus gastronomiques disponibles à ${cityName}, préparés par des chefs à domicile professionnels. Réservez en ligne facilement.`,
  keywords: `menu chef à domicile ${cityName.toLowerCase()}, menu gastronomique ${cityName.toLowerCase()}, cuisine ${cityName}, chef privé ${cityName}`,
  canonical: `https://hometoq.com/trouver-un-chef/menus`, // Page principale sans filtres
});

// Types pour l'autocomplétion
export type SEOPageKey = keyof typeof SEO_DATA;
